import { Loadable } from '@/components/loaders';
import { useUnifiedProfilesValues } from '@/features/unifiedProfiles/context';
import { setSearchPagination } from '@/features/unifiedProfiles/unifiedProfilesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { FrontTable, TablePager } from '@/lib';
import { useGetDataSourcesByIdsQuery } from '@/services/dataSources';
import { useLazyGetUnifiedProfilesQuery } from '@/services/endpoints/unifiedProfiles';
import { useGetUnifiedProfileCountQuery } from '@/services/endpoints/unifiedProfiles/endpoints';
import { UnifiedProfileListResponse } from '@/services/endpoints/unifiedProfiles/types';
import { Row } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnifiedProfileTableItem } from '../types';
import { getUnifiedProfilesTableColumns } from './helper';

const MINIMUM_LOADING_DURATION = 400;

export const UnifiedProfilesTable: FC = () => {
  const { t } = useTranslation('unifiedProfile');
  const workspace = useWorkspace();

  const [getUnifiedProfiles, { data, isLoading: isUnifiedProfilesLoading, isFetching: isUnifiedProfilesFetching }] = useLazyGetUnifiedProfilesQuery();
  const [unifiedProfiles, setUnifiedProfiles] = useState<UnifiedProfileListResponse[]>([]);

  const { searchText } = useUnifiedProfilesValues();
  const validSearchText = searchText && searchText.length > 2 ? searchText : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetUnifiedProfileCountQuery({
    workspaceId: workspace.id,
    searchText: validSearchText,
  });

  const { data: dataSources, isFetching: isDataSourcesFetching } = useGetDataSourcesByIdsQuery(
    {
      workspaceId: workspace.id,
      datasourceIds: [...new Set(unifiedProfiles.flatMap((profile) => profile.datasourceIds))],
    },
    {
      skip: !unifiedProfiles || unifiedProfiles.length === 0,
    }
  );

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getUnifiedProfiles({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
      }),
    [validSearchText]
  );

  const columns = useMemo(() => getUnifiedProfilesTableColumns(t), [t]);
  const tableData = useMemo(
    () =>
      unifiedProfiles.map(
        (profile) =>
          ({
            ...profile,
            id: profile.id[0],
            datasources: dataSources?.filter((ds) => profile.datasourceIds.includes(ds.id)),
          }) satisfies UnifiedProfileTableItem
      ),
    [unifiedProfiles, dataSources]
  );

  const isLoadingDelayed = useDelayedBoolean(isUnifiedProfilesLoading, MINIMUM_LOADING_DURATION);
  const shouldShowLoading = isUnifiedProfilesLoading || isLoadingDelayed;
  const isFetching = isUnifiedProfilesFetching || isDataSourcesFetching;

  const handleRowClick = (row: Row<UnifiedProfileTableItem>) => {
    window.open(`/unified-profiles/${row.original.email}`, '_blank');
  };

  return (
    <TablePager
      stateSelector={(state) => state.unifiedProfiles.ui.search.pagination}
      stateUpdater={setSearchPagination}
      query={queryCallback}
      queryResponse={data ? data.items : undefined}
      setItems={setUnifiedProfiles}
      totalCount={totalCount}
      querying={isTotalCountFetching || isFetching}
      wrapperClassNames='mb-0'
    >
      {(pager) => (
        <Loadable isLoading={shouldShowLoading}>
          <FrontTable tableData={tableData} columns={columns} footer={pager} onRowClick={handleRowClick} />
        </Loadable>
      )}
    </TablePager>
  );
};
